/** Role-aware dashboard (§4.1). Every card links to the list behind it. */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { AlertTriangle, ArrowRight } from 'lucide-react';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { peso, shortDate } from '../lib/format';
import { Alert, Card, EmptyState, Skeleton, StatusChip, inputClass } from '../components/ui';

interface Dashboard {
  period: { from: string; to: string; label: string };
  widgets: {
    pipeline?: {
      openCount: number;
      totalAmount: string;
      weightedAmount: string;
      byStage: { stage: string; count: number; amount: string }[];
    };
    sales?: {
      period: string;
      contractValue: string;
      count: number;
      grossProfit?: string;
      netProfit?: string;
      netMarginPct?: string;
      trend: { month: string; amount: string }[];
    };
    receivables?: {
      outstanding: string;
      buckets: { bucket: string; label: string; amount: string }[];
      topCustomers: { customerId: string; name: string; amount: string }[];
      collectedInPeriod: string;
    };
    payables?: { pendingCount: number; pendingAmount: string; completedInPeriod: string };
    projects?: {
      byStatus: Record<string, number>;
      overdueDeliverables: number;
      overBudget?: { id: string; code: string; name: string; over: string }[];
    };
    recurring?: {
      contracts: number;
      monthlyValue: string;
      dueSoon: { id: string; code: string; customer: string; amount: string; billingDay: number | null }[];
    };
  };
  attention: { kind: string; message: string; link: string }[];
}

const CHART_COLOURS = ['#1E88E5', '#0F2E52', '#38bdf8', '#f59e0b', '#ef4444'];

function Stat({
  label,
  value,
  sub,
  to,
  danger,
}: {
  label: string;
  value: string;
  sub?: string;
  to?: string;
  danger?: boolean;
}): JSX.Element {
  const body = (
    <>
      <p className="text-xs uppercase tracking-wide text-slate-500">{label}</p>
      <p className={`tabular text-2xl font-bold ${danger ? 'text-red-600' : 'text-navy dark:text-white'}`}>
        {value}
      </p>
      {sub && <p className="text-xs text-slate-500">{sub}</p>}
      {to && (
        <span className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-brand">
          Open <ArrowRight aria-hidden="true" className="h-3 w-3" />
        </span>
      )}
    </>
  );

  return to ? (
    <Link
      to={to}
      className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition hover:border-brand dark:border-slate-800 dark:bg-slate-900"
    >
      {body}
    </Link>
  ) : (
    <Card>{body}</Card>
  );
}

export function DashboardPage(): JSX.Element {
  const { user } = useAuth();
  const [period, setPeriod] = useState<'MTD' | 'QTD' | 'YTD'>('MTD');

  const dashboard = useQuery({
    queryKey: ['dashboard', period],
    queryFn: () => api<{ data: Dashboard }>(`/dashboard?period=${period}`),
  });

  if (dashboard.isLoading) return <Skeleton className="h-96 w-full" />;
  const data = dashboard.data?.data;
  if (!data) return <Alert tone="critical">Could not load the dashboard.</Alert>;

  const { widgets } = data;
  const nothingToShow = Object.keys(widgets).length === 0;

  return (
    <div className="mx-auto max-w-7xl space-y-5">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-navy dark:text-white">
            Good day, {user?.name.split(' ')[0]}
          </h1>
          <p className="text-sm text-slate-500">
            {data.period.label} · {shortDate(data.period.from)} to {shortDate(data.period.to)}
          </p>
        </div>
        <select
          className={`${inputClass} w-44`}
          value={period}
          onChange={(event) => setPeriod(event.target.value as typeof period)}
        >
          <option value="MTD">Month to date</option>
          <option value="QTD">Quarter to date</option>
          <option value="YTD">Year to date</option>
        </select>
      </header>

      {data.attention.length > 0 && (
        <Card title="Needs attention">
          <ul className="space-y-2">
            {data.attention.map((item) => (
              <li key={item.kind}>
                <Link
                  to={item.link}
                  className="flex items-center gap-2 text-sm font-medium text-navy hover:underline dark:text-white"
                >
                  <AlertTriangle aria-hidden="true" className="h-4 w-4 text-amber-500" />
                  {item.message}
                </Link>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {nothingToShow && (
        <Card>
          <EmptyState
            title="Nothing to show yet"
            description="Your role has no dashboard widgets. The lists in the sidebar are still available."
          />
        </Card>
      )}

      {widgets.pipeline && (
        <section className="space-y-4">
          <h2 className="text-sm font-bold uppercase tracking-wide text-slate-500">Pipeline</h2>
          <div className="grid gap-4 sm:grid-cols-3">
            <Stat
              label="Open leads"
              value={String(widgets.pipeline.openCount)}
              to="/leads?openOnly=true"
            />
            <Stat
              label="Pipeline value (VAT-ex)"
              value={peso(widgets.pipeline.totalAmount)}
              to="/leads"
            />
            <Stat
              label="Weighted pipeline"
              value={peso(widgets.pipeline.weightedAmount)}
              sub="Amount × stage probability"
            />
          </div>
          {widgets.pipeline.byStage.length > 0 && (
            <Card title="Leads by stage">
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={widgets.pipeline.byStage}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                    <XAxis dataKey="stage" tick={{ fontSize: 11 }} interval={0} angle={-15} dy={10} height={60} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={(value: number) => `${value / 1000}k`} />
                    <Tooltip
                      formatter={(value: number) => peso(value)}
                      labelStyle={{ fontWeight: 600 }}
                    />
                    <Bar dataKey="amount" radius={[6, 6, 0, 0]}>
                      {widgets.pipeline.byStage.map((_, index) => (
                        <Cell key={index} fill={CHART_COLOURS[index % CHART_COLOURS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card>
          )}
        </section>
      )}

      {widgets.sales && (
        <section className="space-y-4">
          <h2 className="text-sm font-bold uppercase tracking-wide text-slate-500">Sales</h2>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Stat
              label="Contracts confirmed"
              value={peso(widgets.sales.contractValue)}
              sub={`${widgets.sales.count} order(s) this period`}
              to="/sales-orders"
            />
            {widgets.sales.grossProfit !== undefined && (
              <Stat label="Gross profit" value={peso(widgets.sales.grossProfit)} />
            )}
            {widgets.sales.netProfit !== undefined && (
              <Stat
                label="Net profit"
                value={peso(widgets.sales.netProfit)}
                sub={`${widgets.sales.netMarginPct}% net margin`}
                danger={Number(widgets.sales.netProfit) < 0}
              />
            )}
            {widgets.recurring && (
              <Stat
                label="Monthly recurring"
                value={peso(widgets.recurring.monthlyValue)}
                sub={`${widgets.recurring.contracts} contract(s)`}
              />
            )}
          </div>
          {widgets.sales.trend.length > 0 && (
            <Card title="Contract value, last 12 months">
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={widgets.sales.trend}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={(value: number) => `${value / 1000}k`} />
                    <Tooltip formatter={(value: number) => peso(value)} />
                    <Line type="monotone" dataKey="amount" stroke="#1E88E5" strokeWidth={2} dot />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </Card>
          )}
        </section>
      )}

      {widgets.receivables && (
        <section className="space-y-4">
          <h2 className="text-sm font-bold uppercase tracking-wide text-slate-500">Receivables</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <Stat
              label="Outstanding"
              value={peso(widgets.receivables.outstanding)}
              to="/reports/ar-aging"
            />
            <Stat
              label="Collected this period"
              value={peso(widgets.receivables.collectedInPeriod)}
              to="/billing"
            />
          </div>
          <div className="grid gap-4 lg:grid-cols-2">
            <Card title="Ageing">
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={widgets.receivables.buckets}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                    <XAxis dataKey="label" tick={{ fontSize: 10 }} interval={0} angle={-12} dy={8} height={50} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={(value: number) => `${value / 1000}k`} />
                    <Tooltip formatter={(value: number) => peso(value)} />
                    <Bar dataKey="amount" radius={[6, 6, 0, 0]}>
                      {widgets.receivables.buckets.map((bucket, index) => (
                        <Cell
                          key={bucket.bucket}
                          fill={index >= 3 ? '#ef4444' : CHART_COLOURS[index % CHART_COLOURS.length]}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card>
            <Card title="Top customers by balance">
              {widgets.receivables.topCustomers.length === 0 ? (
                <EmptyState title="Nothing outstanding" />
              ) : (
                <ul className="divide-y divide-slate-100 text-sm dark:divide-slate-800">
                  {widgets.receivables.topCustomers.map((customer) => (
                    <li key={customer.customerId} className="flex justify-between py-2">
                      <Link to={`/customers/${customer.customerId}`} className="hover:underline">
                        {customer.name}
                      </Link>
                      <span className="tabular font-semibold">{peso(customer.amount)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          </div>
        </section>
      )}

      {(widgets.payables || widgets.projects) && (
        <section className="space-y-4">
          <h2 className="text-sm font-bold uppercase tracking-wide text-slate-500">
            Delivery and costs
          </h2>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {widgets.payables && (
              <>
                <Stat
                  label="Payables pending"
                  value={peso(widgets.payables.pendingAmount)}
                  sub={`${widgets.payables.pendingCount} entr${widgets.payables.pendingCount === 1 ? 'y' : 'ies'}`}
                  to="/payables"
                />
                <Stat
                  label="Paid this period"
                  value={peso(widgets.payables.completedInPeriod)}
                  to="/reports/ap"
                />
              </>
            )}
            {widgets.projects && (
              <>
                <Stat
                  label="Projects ongoing"
                  value={String(widgets.projects.byStatus.ONGOING ?? 0)}
                  sub={`${widgets.projects.byStatus.PENDING ?? 0} pending · ${widgets.projects.byStatus.COMPLETED ?? 0} completed`}
                  to="/projects"
                />
                <Stat
                  label="Overdue deliverables"
                  value={String(widgets.projects.overdueDeliverables)}
                  danger={widgets.projects.overdueDeliverables > 0}
                  to="/projects"
                />
              </>
            )}
          </div>
          {widgets.projects?.overBudget && widgets.projects.overBudget.length > 0 && (
            <Card title="Projects over budget">
              <ul className="divide-y divide-slate-100 text-sm dark:divide-slate-800">
                {widgets.projects.overBudget.map((project) => (
                  <li key={project.id} className="flex items-center justify-between py-2">
                    <Link to={`/projects/${project.id}`} className="hover:underline">
                      {project.name}{' '}
                      <span className="font-mono text-xs text-slate-500">{project.code}</span>
                    </Link>
                    <StatusChip tone="critical">{peso(project.over)} over</StatusChip>
                  </li>
                ))}
              </ul>
            </Card>
          )}
        </section>
      )}

      {widgets.recurring && widgets.recurring.dueSoon.length > 0 && (
        <Card title="Recurring billings due in the next 7 days">
          <ul className="divide-y divide-slate-100 text-sm dark:divide-slate-800">
            {widgets.recurring.dueSoon.map((order) => (
              <li key={order.id} className="flex items-center justify-between py-2">
                <Link to={`/sales-orders/${order.id}`} className="hover:underline">
                  {order.customer}{' '}
                  <span className="font-mono text-xs text-slate-500">{order.code}</span>
                </Link>
                <span className="tabular">
                  {peso(order.amount)}{' '}
                  <span className="text-xs text-slate-500">on day {order.billingDay}</span>
                </span>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}
